import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, Send, X, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

type Msg = { role: 'user' | 'assistant'; content: string };

const STORAGE_KEY = 'cycy.chat.history';
const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-assistant`;

const SUGGESTIONS = [
  'Welche Geschenke habt ihr für Damen?',
  'Wie funktioniert die Personalisierung mit Foto?',
  'Wie lange dauert der Versand?',
  'Habt ihr Angebote?',
];

const WELCOME: Msg = {
  role: 'assistant',
  content:
    'Hallo! 👋 Ich bin dein KI-Assistent von **cycy-art**. Ich helfe dir bei Fragen zu Produkten, Personalisierung, Versand und mehr. Wonach suchst du?',
};

// Tiny safe-ish markdown renderer: bold, links, line breaks, lists.
function renderMarkdown(text: string): string {
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  let html = escape(text);
  // links [label](url) — only allow safe schemes
  html = html.replace(
    /\[([^\]]+)\]\(([^)\s]+)\)/g,
    (_m, label, url) => {
      const safeUrl = /^(https?:|mailto:|\/|#)/i.test(url) ? url : '#';
      const encodedUrl = safeUrl
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return `<a href="${encodedUrl}" rel="noopener noreferrer nofollow" target="_blank" class="text-primary underline underline-offset-2 hover:opacity-80">${label}</a>`;
    },
  );
  // bold **x**
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  // bullet lines
  html = html.replace(/(^|\n)- (.+)/g, '$1<li>$2</li>');
  html = html.replace(/(<li>[\s\S]*?<\/li>)(\n(?!<li>)|$)/g, '<ul class="list-disc pl-5 my-1 space-y-0.5">$1</ul>$2');
  html = html.replace(/\n/g, '<br/>');
  return html;
}

export default function ChatAssistant() {
  const location = useLocation();
  const hidden = location.pathname.startsWith('/admin');
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>(() => {
    if (typeof window === 'undefined') return [WELCOME];
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Msg[];
        if (Array.isArray(parsed) && parsed.length) return parsed;
      }
    } catch {/* noop */}
    return [WELCOME];
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-30)));
    } catch {/* noop */}
  }, [messages]);

  useEffect(() => {
    if (open) requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: 99999, behavior: 'smooth' }));
  }, [messages, open]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;
    setError(null);
    const userMsg: Msg = { role: 'user', content: trimmed };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput('');
    setLoading(true);

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    let assistantText = '';
    const upsert = (chunk: string) => {
      assistantText += chunk;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && last !== WELCOME && prev.length > next.length - 0) {
          // last message is the assistant we're streaming into
          return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: assistantText } : m));
        }
        return [...prev, { role: 'assistant', content: assistantText }];
      });
    };

    try {
      const resp = await fetch(CHAT_URL, {
        method: 'POST',
        signal: ctrl.signal,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          messages: next.map(({ role, content }) => ({ role, content })),
        }),
      });

      if (!resp.ok || !resp.body) {
        let msg = 'Der Assistent ist gerade nicht erreichbar.';
        try {
          const j = await resp.json();
          if (j?.error) msg = j.error;
        } catch {/* noop */}
        if (resp.status === 429) msg = 'Zu viele Anfragen – bitte kurz warten.';
        if (resp.status === 402) msg = 'KI-Guthaben aufgebraucht.';
        throw new Error(msg);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let done = false;
      while (!done) {
        const r = await reader.read();
        if (r.done) break;
        buf += decoder.decode(r.value, { stream: true });
        let nl: number;
        while ((nl = buf.indexOf('\n')) !== -1) {
          let line = buf.slice(0, nl);
          buf = buf.slice(nl + 1);
          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (!line || line.startsWith(':')) continue;
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (payload === '[DONE]') { done = true; break; }
          try {
            const parsed = JSON.parse(payload);
            const delta = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (delta) upsert(delta);
          } catch {
            buf = line + '\n' + buf;
            break;
          }
        }
      }
    } catch (e: any) {
      if (e?.name === 'AbortError') return;
      setError(e?.message ?? 'Fehler beim Senden.');
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  const reset = () => {
    abortRef.current?.abort();
    setMessages([WELCOME]);
    setError(null);
  };

  const showSuggestions = useMemo(() => messages.length <= 1, [messages]);

  if (hidden) return null;

  return (
    <>
      {/* Floating launcher */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Chat schließen' : 'Chat öffnen'}
        className={cn(
          'fixed z-[60] flex items-center justify-center rounded-full shadow-lg transition-all',
          'bottom-[calc(env(safe-area-inset-bottom)+5rem)] right-3 h-12 w-12',
          'sm:bottom-4 sm:right-4 sm:h-14 sm:w-14',
          'bg-primary text-primary-foreground hover:scale-105 active:scale-95',
          open && 'rotate-90',
        )}
      >
        {open ? <X className="h-5 w-5 sm:h-6 sm:w-6" /> : <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          className={cn(
            'fixed z-[59] flex flex-col overflow-hidden border border-border bg-background shadow-2xl',
            'bottom-2 right-2 left-2 sm:left-auto sm:w-[380px] sm:right-4 sm:bottom-20',
            'h-[min(80vh,560px)] rounded-2xl',
          )}
          role="dialog"
          aria-label="cycy-art Assistent"
        >
          <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold leading-tight">cycy-art Assistent</p>
              <p className="text-xs text-muted-foreground leading-tight">Beantwortet Fragen zu Produkten & Bestellung</p>
            </div>
            <Button variant="ghost" size="sm" onClick={reset} className="text-xs">Neu</Button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                className={cn(
                  'flex',
                  m.role === 'user' ? 'justify-end' : 'justify-start',
                )}
              >
                <div
                  className={cn(
                    'max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed',
                    m.role === 'user'
                      ? 'bg-primary text-primary-foreground rounded-br-sm'
                      : 'bg-muted text-foreground rounded-bl-sm',
                  )}
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }}
                />
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Assistent denkt nach…
              </div>
            )}
            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                {error}
              </div>
            )}

            {showSuggestions && (
              <div className="pt-1">
                <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Vorschläge</p>
                <div className="flex flex-wrap gap-1.5">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="rounded-full border border-border bg-card px-2.5 py-1 text-xs text-foreground hover:bg-accent transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(input); }}
            className="flex items-center gap-2 border-t border-border bg-background px-3 py-2"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Frage stellen…"
              className="h-9 text-sm"
              disabled={loading}
            />
            <Button type="submit" size="icon" className="h-9 w-9 shrink-0" disabled={loading || !input.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
